// © Thibault Sabathier, 2025. Tous droits réservés.
// frontend/src/layout/AdminLayout.js

import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { post as apiPost } from '../utils/apiClient'
import {
  CContainer,
  CSidebar,
  CSidebarBrand,
  CSidebarNav,
  CSidebarToggler,
  CHeader,
  CHeaderToggler,
  CHeaderBrand,
  CHeaderNav,
  CNavItem,
  CNavLink,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CDropdownDivider,
  CAvatar,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilMenu,
  cilSpeedometer,
  cilBuilding,
  cilBell,
  cilSettings,
  cilAccountLogout,
  cilUser,
  cilTask,
} from '@coreui/icons'
import { AppSidebarNav } from './AppSidebarNav'

// Navigation items pour le sidebar admin
const adminNavItems = [
  {
    component: 'CNavItem',
    name: 'Dashboard',
    to: '/admin',
    icon: <CIcon icon={cilSpeedometer} customClassName="nav-icon" />,
  },
  {
    component: 'CNavItem',
    name: 'Organisations',
    to: '/admin/organizations',
    icon: <CIcon icon={cilBuilding} customClassName="nav-icon" />,
  },
  {
    component: 'CNavItem',
    name: 'Jobs de Déploiement',
    to: '/admin/deploy-jobs',
    icon: <CIcon icon={cilTask} customClassName="nav-icon" />,
  },
  {
    component: 'CNavItem',
    name: 'Webhooks',
    to: '/admin/webhooks',
    icon: <CIcon icon={cilBell} customClassName="nav-icon" />,
  },
  {
    component: 'CNavItem',
    name: 'Système',
    to: '/admin/system',
    icon: <CIcon icon={cilSettings} customClassName="nav-icon" />,
  },
]

const AdminLayout = ({ children }) => {
  const [sidebarShow, setSidebarShow] = useState(true)
  const [sidebarUnfoldable, setSidebarUnfoldable] = useState(false)
  const navigate = useNavigate()

  // Récupérer les infos admin depuis localStorage
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}')

  const handleLogout = async () => {
    // ✅ SECURITY: Appel API pour clear le cookie httpOnly côté serveur
    try { await apiPost('/admin/logout', {}) } catch { /* ignore */ }
    localStorage.removeItem('adminUser')
    localStorage.removeItem('adminUserId')
    navigate('/admin/login')
  }

  return (
    <div>
      {/* Sidebar */}
      <CSidebar
        position="fixed"
        unfoldable={sidebarUnfoldable}
        visible={sidebarShow}
        onVisibleChange={(visible) => setSidebarShow(visible)}
        className="border-end"
      >
        <CSidebarBrand className="d-none d-md-flex" to="/admin">
          <div
            className="sidebar-brand-full"
            style={{
              padding: '1rem',
              fontSize: '1.25rem',
              fontWeight: '700',
              color: 'var(--cui-primary)',
            }}
          >
            <span style={{ color: '#3399ff' }}>Terrilink</span> Admin
          </div>
          <div
            className="sidebar-brand-narrow"
            style={{
              padding: '1rem',
              fontSize: '1.25rem',
              fontWeight: '700',
              color: 'var(--cui-primary)',
            }}
          >
            T
          </div>
        </CSidebarBrand>

        <CSidebarNav>
          <AppSidebarNav items={adminNavItems} />
        </CSidebarNav>

        <CSidebarToggler
          className="d-none d-lg-flex"
          onClick={() => setSidebarUnfoldable(!sidebarUnfoldable)}
        />
      </CSidebar>

      {/* Header */}
      <div className="wrapper d-flex flex-column min-vh-100 bg-light">
        <CHeader position="sticky" className="mb-4">
          <CContainer fluid>
            <CHeaderToggler className="ps-1" onClick={() => setSidebarShow(!sidebarShow)}>
              <CIcon icon={cilMenu} size="lg" />
            </CHeaderToggler>

            <CHeaderBrand className="mx-auto d-md-none" to="/admin">
              <span style={{ fontWeight: '700', color: '#3399ff' }}>Terrilink Admin</span>
            </CHeaderBrand>

            <CHeaderNav className="ms-auto">
              <CDropdown variant="nav-item">
                <CDropdownToggle placement="bottom-end" className="py-0" caret={false}>
                  <CAvatar color="primary" textColor="white" size="md">
                    {adminUser.first_name?.[0]}
                    {adminUser.last_name?.[0]}
                  </CAvatar>
                </CDropdownToggle>
                <CDropdownMenu className="pt-0" placement="bottom-end">
                  <CDropdownItem header className="bg-light fw-semibold py-2">
                    Super Admin
                  </CDropdownItem>
                  <CDropdownItem>
                    <CIcon icon={cilUser} className="me-2" />
                    {adminUser.email}
                  </CDropdownItem>
                  <CDropdownDivider />
                  <CDropdownItem onClick={handleLogout} style={{ cursor: 'pointer' }}>
                    <CIcon icon={cilAccountLogout} className="me-2" />
                    Déconnexion
                  </CDropdownItem>
                </CDropdownMenu>
              </CDropdown>
            </CHeaderNav>
          </CContainer>
        </CHeader>

        {/* Body */}
        <div className="body flex-grow-1 px-3">
          <CContainer lg>{children}</CContainer>
        </div>
      </div>
    </div>
  )
}

export default AdminLayout
